import React, { CSSProperties, FC, useEffect, useMemo, useState } from "react";
import { useSigma } from "react-sigma-v2";
import { MdCategory } from "react-icons/md";
import { keyBy, mapValues, sortBy, values } from "lodash";
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";

import { FiltersState, WymmoTag, get_tag_group_color } from "../types";
import Panel from "./Panel";

const TagsPanel: FC<{
  tags: WymmoTag[];
  filters: FiltersState;
  iconsRoot: string;
  toggleTag: (tag: string) => void;
  setTags: (tags: Record<string, boolean>) => void;
}> = ({ tags, filters, iconsRoot, toggleTag, setTags }) => {
  const sigma = useSigma();
  const graph = sigma.getGraph();

  const nodesPerTag = useMemo(() => {
    const index: Record<string, number> = {};
    graph.forEachNode((_, { tag }) => (index[tag] = (index[tag] || 0) + 1));
    return index;
  }, []);

  const maxNodesPerTag = useMemo(
    () => Math.max(...values(nodesPerTag)),
    [nodesPerTag]
  );
  const visibleTagsCount = useMemo(
    () => Object.keys(filters.tags).length,
    [filters]
  );

  const [visibleNodesPerTag, setVisibleNodesPerTag] =
    useState<Record<string, number>>(nodesPerTag);
  useEffect(() => {
    // To ensure the graphology instance has up to data "hidden" values for
    // nodes, we wait for next frame before reindexing. This won't matter in the
    // UX, because of the visible nodes bar width transition.
    requestAnimationFrame(() => {
      const index: Record<string, number> = {};
      graph.forEachNode(
        (_, { tag, hidden }) => !hidden && (index[tag] = (index[tag] || 0) + 1)
      );
      setVisibleNodesPerTag(index);
    });
  }, [filters]);

  const sortedTags = useMemo(
    () =>
      sortBy(tags, (tag) =>
        tag.title
        // tag.key === "unknown" ? Infinity : -nodesPerTag[tag.key]
      ),
    [tags, nodesPerTag]
  );

  return (
    <Panel
      title={
        <>
          <MdCategory className="text-muted" /> Catégories
          {visibleTagsCount < tags.length ? (
            <span className="text-muted text-small">
              {" "}
              ({visibleTagsCount} / {tags.length})
            </span>
          ) : (
            ""
          )}
        </>
      }
    >
      <p>
        <i className="text-muted">
          Cliquez sur une catégorie pour la cacher/montrer.
        </i>
      </p>
      <p className="buttons">
        <button
          className="btn"
          onClick={() => setTags(mapValues(keyBy(tags, "key"), () => true))}
        >
          <AiOutlineCheckCircle /> Tout séléctionner
        </button>{" "}
        <button className="btn" onClick={() => setTags({})}>
          <AiOutlineCloseCircle /> Tout désélectionner
        </button>
      </p>
      <ul>
        {sortedTags.map((tag) => {
          const nodesCount = nodesPerTag[tag.key];
          const visibleNodesCount = visibleNodesPerTag[tag.key] || 0;
          const tag_color = get_tag_group_color(tag.tag_group);

          let span_style: CSSProperties = {
            backgroundColor: tag_color,
            borderColor: tag_color
          };

          if (tag.icon) {
            span_style.backgroundImage = `url(${iconsRoot}/${tag.icon}`;
          }

          return (
            <li
              className="caption-row"
              key={tag.key}
              title={`${nodesCount} page${nodesCount > 1 ? "s" : ""}${visibleNodesCount !== nodesCount
                ? ` (only ${visibleNodesCount} visible)`
                : ""
                }`}
            >
              <input
                type="checkbox"
                checked={filters.tags[tag.key] || false}
                onChange={() => toggleTag(tag.key)}
                id={`tag-${tag.key}`}
              />
              <label htmlFor={`tag-${tag.key}`}>
                <span className="circle" style={span_style} />{" "}
                <div className="node-label">
                  <span>{tag.title}</span>
                  <div
                    className="bar"
                    style={{ width: (100 * nodesCount) / maxNodesPerTag + "%" }}
                  >
                    <div
                      className="inside-bar"
                      style={{
                        width: (100 * visibleNodesCount) / nodesCount + "%",
                      }}
                    />
                  </div>
                </div>
              </label>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
};

export default TagsPanel;
